/**
 * Add a winner (screen 10), for admins. Pick the square by entering both
 * numbers or on the grid (with the holder shown), then a label: one of the
 * pool's checkpoints, or Custom ("Reverse final"). A checkpoint label replaces
 * the automatic winner for that checkpoint on this grid; a custom label adds
 * one. The server resolves the digits to a square and the holder.
 */
import {
  buildSquaresGridShape,
  findSquareForDigits,
} from "@sportsmet/contest-engine";
import {
  useSquaresGridGet,
  useSquaresWinnerAdd,
} from "@sportsmet/contest-sdk/private";
import {
  Button,
  Dialog,
  Input,
  Select,
  SegmentedControl,
  Skeleton,
} from "@sportsmet/ui";
import { type ReactNode, useEffect, useState } from "react";

import {
  confirmSquaresDiscard,
  runSquaresMutation,
  useRefreshSquaresPoolLine,
} from "./squaresActions";
import { SquaresBoard } from "./SquaresBoard";
import { SquaresChipGroup } from "./SquaresChipGroup";
import {
  SQUARES_ADD_GRID_CANCEL,
  SQUARES_ADD_WINNER_ADDS,
  SQUARES_ADD_WINNER_BY_NUMBERS,
  SQUARES_ADD_WINNER_CUSTOM,
  SQUARES_ADD_WINNER_CUSTOM_LABEL,
  SQUARES_ADD_WINNER_CUSTOM_PLACEHOLDER,
  SQUARES_ADD_WINNER_DIGIT_ERROR,
  SQUARES_ADD_WINNER_GAME,
  SQUARES_ADD_WINNER_HOLDER,
  SQUARES_ADD_WINNER_HOW,
  SQUARES_ADD_WINNER_LABEL_HEADING,
  SQUARES_ADD_WINNER_ON_GRID,
  SQUARES_ADD_WINNER_PICK_PROMPT,
  SQUARES_ADD_WINNER_REPLACES,
  SQUARES_ADD_WINNER_SUBMIT,
  SQUARES_ADD_WINNER_TITLE,
  SQUARES_WINNING_UNCLAIMED,
  squaresWinnerDigitFieldLabel,
} from "./squaresCopy";
import { buildSquaresBoardLayout } from "./squaresModel";
import type { typeSquaresWinnerLabelKey } from "./squaresTypes";

export type typeSquaresAddWinnerSheetProps = {
  open: boolean;
  onClose: () => void;
  poolId: string;
  gridId: string;
  eventId: string | null;
  /** Names a game for the game picker, from the parent's team names. */
  formatEvent: (event: {
    eventId: string;
    awayTeamId: string;
    homeTeamId: string;
  }) => string;
};

type typePickMode = "numbers" | "grid";

const CUSTOM_KEY = "custom";

/** The square two entered digits name, or null before the numbers are drawn. */
export function squaresKeyForDigits(input: {
  data: {
    grid: {
      size: number;
      rowNumbers?: readonly number[] | null;
      colNumbers?: readonly number[] | null;
    };
  } | null;
  topDigit: number | null;
  sideDigit: number | null;
}): string | null {
  const grid = input.data?.grid ?? null;
  if (
    grid === null ||
    input.topDigit === null ||
    input.sideDigit === null ||
    grid.rowNumbers === null ||
    grid.rowNumbers === undefined ||
    grid.colNumbers === null ||
    grid.colNumbers === undefined ||
    (grid.size !== 25 && grid.size !== 50 && grid.size !== 100)
  ) {
    return null;
  }
  return findSquareForDigits({
    shape: buildSquaresGridShape(grid.size),
    numbers: {
      rowNumbers: [...grid.rowNumbers],
      colNumbers: [...grid.colNumbers],
    },
    topDigit: input.topDigit,
    sideDigit: input.sideDigit,
  });
}

/** One digit, 0 to 9, or null for anything else. */
export function parseSquaresDigit(text: string): number | null {
  return /^\d$/.test(text.trim()) ? Number(text.trim()) : null;
}

export const SquaresAddWinnerSheet = (
  props: typeSquaresAddWinnerSheetProps,
): ReactNode => {
  const [mode, setMode] = useState<typePickMode>("numbers");
  const refreshPoolLine = useRefreshSquaresPoolLine(props.poolId);
  const [topText, setTopText] = useState("");
  const [sideText, setSideText] = useState("");
  const [pickedKey, setPickedKey] = useState<string | null>(null);
  const [labelKey, setLabelKey] = useState<string | null>(null);
  const [customLabel, setCustomLabel] = useState("");
  const [eventId, setEventId] = useState<string | null>(props.eventId);
  const [isBusy, setBusy] = useState(false);

  useEffect(() => {
    if (!props.open) return;
    setMode("numbers");
    setTopText("");
    setSideText("");
    setPickedKey(null);
    setLabelKey(null);
    setCustomLabel("");
    setEventId(props.eventId);
  }, [props.open, props.eventId]);

  const gridQuery = useSquaresGridGet(
    {
      poolId: props.poolId,
      gridId: props.gridId,
      ...(eventId === null ? {} : { eventId }),
    },
    { enabled: props.open && props.gridId !== "" },
  );
  const data = gridQuery.data?.data ?? null;
  const winnerAdd = useSquaresWinnerAdd();
  const resolvedEventId =
    eventId ?? data?.selectedEventId ?? data?.events[0]?.eventId ?? null;
  // ISSUE-1282. A custom game has no checkpoints, so Custom is the only label.
  const hasNoCheckpoints = data !== null && data.checkpointRows.length === 0;
  useEffect(() => {
    if (props.open && hasNoCheckpoints) setLabelKey(CUSTOM_KEY);
  }, [props.open, hasNoCheckpoints]);

  const topDigit = parseSquaresDigit(topText);
  const sideDigit = parseSquaresDigit(sideText);
  const hasSquare =
    mode === "grid"
      ? pickedKey !== null
      : topDigit !== null && sideDigit !== null;
  const hasLabel =
    labelKey !== null && (labelKey !== CUSTOM_KEY || customLabel.trim() !== "");
  const isReady = hasSquare && hasLabel && resolvedEventId !== null;
  const isDirty =
    topText !== "" ||
    sideText !== "" ||
    pickedKey !== null ||
    customLabel.trim() !== "";

  // ISSUE-1272. Entered numbers preview their square's holder too, once the
  // numbers are drawn, through the engine's own digit lookup. The server
  // still resolves the square on save.
  const numbersKey = squaresKeyForDigits({ data, topDigit, sideDigit });
  const previewKey = mode === "grid" ? pickedKey : numbersKey;
  const pickedHolder =
    previewKey === null || data === null
      ? null
      : (data.squares.find((square) => square.squareKey === previewKey) ??
        null);

  async function submit(): Promise<void> {
    if (!isReady || labelKey === null || resolvedEventId === null) return;
    setBusy(true);
    const result = await runSquaresMutation(
      () =>
        winnerAdd.mutateAsync({
          poolId: props.poolId,
          gridId: props.gridId,
          eventId: resolvedEventId,
          ...(mode === "grid"
            ? { squareKey: pickedKey }
            : { topDigit, sideDigit }),
          labelKey: labelKey as typeSquaresWinnerLabelKey,
          ...(labelKey === CUSTOM_KEY
            ? { customLabel: customLabel.trim() }
            : {}),
        }),
      refreshPoolLine,
    );
    setBusy(false);
    if (result !== null) props.onClose();
  }

  const topLabel = data?.grid.axes.topLabel ?? "";
  const sideLabel = data?.grid.axes.sideLabel ?? "";

  return (
    <Dialog
      open={props.open}
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
      title={SQUARES_ADD_WINNER_TITLE}
      description={data?.grid.label}
      isDirty={isDirty}
      onRequestClose={() => {
        void (async () => {
          if (await confirmSquaresDiscard("winner")) props.onClose();
        })();
      }}
      footer={
        <>
          <Button variant="secondary" onClick={props.onClose}>
            {SQUARES_ADD_GRID_CANCEL}
          </Button>
          <Button
            disabled={!isReady}
            loading={isBusy}
            data-testid="squares-add-winner-submit"
            onClick={() => {
              void submit();
            }}
          >
            {SQUARES_ADD_WINNER_SUBMIT}
          </Button>
        </>
      }
    >
      {data === null ? (
        <div
          className="flex flex-col gap-2"
          data-testid="squares-add-winner-loading"
        >
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </div>
      ) : (
        <div
          className="flex flex-col gap-4"
          data-testid="squares-add-winner-sheet"
        >
          {data.events.length > 1 ? (
            <Select
              label={SQUARES_ADD_WINNER_GAME}
              value={resolvedEventId ?? undefined}
              items={data.events.map((event) => ({
                value: event.eventId,
                label: props.formatEvent(event),
              }))}
              onValueChange={setEventId}
            />
          ) : null}
          <SegmentedControl
            label={SQUARES_ADD_WINNER_HOW}
            fullWidth
            value={mode}
            testId="squares-add-winner-mode"
            options={[
              { value: "numbers", label: SQUARES_ADD_WINNER_BY_NUMBERS },
              { value: "grid", label: SQUARES_ADD_WINNER_ON_GRID },
            ]}
            onValueChange={(value) => {
              setMode(value === "grid" ? "grid" : "numbers");
            }}
          />
          {mode === "numbers" ? (
            <div className="grid grid-cols-2 gap-3">
              <Input
                label={squaresWinnerDigitFieldLabel(topLabel)}
                inputMode="numeric"
                maxLength={1}
                value={topText}
                data-testid="squares-add-winner-top"
                error={
                  topText !== "" && topDigit === null
                    ? SQUARES_ADD_WINNER_DIGIT_ERROR
                    : undefined
                }
                onChange={(event) => {
                  setTopText(event.target.value);
                }}
              />
              <Input
                label={squaresWinnerDigitFieldLabel(sideLabel)}
                inputMode="numeric"
                maxLength={1}
                value={sideText}
                data-testid="squares-add-winner-side"
                error={
                  sideText !== "" && sideDigit === null
                    ? SQUARES_ADD_WINNER_DIGIT_ERROR
                    : undefined
                }
                onChange={(event) => {
                  setSideText(event.target.value);
                }}
              />
              {numbersKey === null ? null : (
                <p
                  className="col-span-2 text-body text-ink"
                  data-testid="squares-add-winner-holder"
                >
                  {`${SQUARES_ADD_WINNER_HOLDER}: ${pickedHolder?.displayName ?? SQUARES_WINNING_UNCLAIMED}`}
                </p>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <p className="text-caption text-ink-secondary">
                {SQUARES_ADD_WINNER_PICK_PROMPT}
              </p>
              <SquaresBoard
                testId="squares-add-winner-board"
                layout={buildSquaresBoardLayout({
                  data,
                  selectedEventId: resolvedEventId,
                })}
                topLabel={topLabel}
                sideLabel={sideLabel}
                pickedKey={pickedKey}
                isCellEnabled={() => true}
                onCellPress={(cell) => {
                  setPickedKey(cell.squareKey);
                }}
              />
              {pickedKey === null ? null : (
                <p
                  className="text-body text-ink"
                  data-testid="squares-add-winner-holder"
                >
                  {`${SQUARES_ADD_WINNER_HOLDER}: ${pickedHolder?.displayName ?? SQUARES_WINNING_UNCLAIMED}`}
                </p>
              )}
            </div>
          )}
          <section className="flex flex-col gap-2">
            <h3 className="text-body-strong text-ink">
              {SQUARES_ADD_WINNER_LABEL_HEADING}
            </h3>
            <SquaresChipGroup
              mode="single"
              label={SQUARES_ADD_WINNER_LABEL_HEADING}
              testId="squares-add-winner-labels"
              value={labelKey}
              options={[
                ...data.checkpointRows.map((row) => ({
                  value: row.checkpointKey,
                  label: row.label,
                })),
                { value: CUSTOM_KEY, label: SQUARES_ADD_WINNER_CUSTOM },
              ]}
              onChange={setLabelKey}
            />
            {labelKey === CUSTOM_KEY ? (
              <Input
                label={SQUARES_ADD_WINNER_CUSTOM_LABEL}
                placeholder={SQUARES_ADD_WINNER_CUSTOM_PLACEHOLDER}
                maxLength={40}
                value={customLabel}
                data-testid="squares-add-winner-custom"
                onChange={(event) => {
                  setCustomLabel(event.target.value);
                }}
              />
            ) : null}
            {labelKey === null ? null : (
              <p className="text-caption text-ink-secondary">
                {labelKey === CUSTOM_KEY
                  ? SQUARES_ADD_WINNER_ADDS
                  : SQUARES_ADD_WINNER_REPLACES}
              </p>
            )}
          </section>
        </div>
      )}
    </Dialog>
  );
};
